import { toast } from "react-toastify";
import { deleteItems, updateStateData } from "../Store/actions";
import store from "../Store/store";
import { sendApiRequest } from "./requestConfig";
import { validateAlphabets, validateEmpty, validateEmailFormat } from "./utils";
import { loadCampaigns } from "./campaignRequests";
import { fetchBrandFilters } from "./brandReport";
import { checkUserDetailsFromCookie, checkUserRole } from "./userManagement";
import { canEditAcrossCompanies } from "./crossCompanyAccess";
import {
  buildAnalysisGroupKeysForSections,
  buildRegionKeysForSections,
  buildNielsenKeysForSections,
} from "./dashboardFilterSeed";

// to check the available report sections wrt workspace and also the selected settings for a particular campaign
export const fetchReportSettings = async (campaignId, defaultSettingDeviation) => {
  // call api
  const fetchSettings = await sendApiRequest("reports/settings/" + campaignId, "", "GET");
  //   evaluate api response
  if (typeof fetchSettings.success !== "undefined" && fetchSettings.success === true) {

    store.dispatch(
      updateStateData({
        report_settings: {
          available_options: [...fetchSettings.response.visible_sections, ...fetchSettings.response.available_sections],
          visible_options: fetchSettings.response.visible_sections.filter((visibleOpt) => visibleOpt.type !== "impression").map((visibleOpt) => visibleOpt.id),
          marked_as_default: fetchSettings.response.marked_as_default,
          published: fetchSettings.response.published,
        },
      })
    );
    return {
      success: true,
    };
  } else {
    return {
      success: false,
    };
  }
};

// to save visible options for campaign analytics report.
export const saveVisibleSectionsForCampaign = async (campaignId) => {
  const userStateData = store.getState().userSelections;
  let visibleSectionIds = [...userStateData.report_settings.available_options.filter(sec => sec.type === "impression")
    .map(section => section.id), ...userStateData.report_settings.visible_options];

  let requestBody = {
    id_visible_sections: [...new Set(visibleSectionIds)],
    marked_as_default: userStateData.report_settings.marked_as_default,
  };

  if (typeof userStateData.report_settings.report_status_updated !== "undefined" && userStateData.report_settings.report_status_updated === true) {
    requestBody = {
      ...requestBody,
      published: typeof userStateData.report_settings.published !== "undefined" ? userStateData.report_settings.published : false
    };
  }

  // call api
  const saveSettings = await sendApiRequest("reports/settings/save/" + campaignId, requestBody, "POST");
  //   evaluate api response
  if (typeof saveSettings.success !== "undefined" && saveSettings.success === true) {
    if (typeof userStateData.report_settings.published !== "undefined") {
      loadCampaigns(); //campaign will only need to reload when the status is updated.
    }

    resetReportSettings();
    if (typeof userStateData.report_settings.published !== "undefined" && userStateData.report_settings.published === true) {
      toast("Report published successfully", { type: "success" });
    } else {
      toast("Report settings saved successfully", { type: "success" });
    }

    return {
      success: true,
    };
  } else {
    toast("Sorry, something went wrong.", { type: "error" });
    return {
      success: false,
    };
  }
};

// to clear the options selected in visible list
export const clearSectionsList = () => {
  const userStateData = store.getState().userSelections;
  let tabNameArray = {};
  let tabTypeArray = {};
  userStateData.report_settings.workspace_options.forEach((workSpaceOpt) => {
    tabNameArray = { ...tabNameArray, [workSpaceOpt.tab_text]: workSpaceOpt.tab_name };
    tabTypeArray = { ...tabTypeArray, [workSpaceOpt.tab_text]: workSpaceOpt.tab_type };
  });
  store.dispatch(
    updateStateData({
      report_settings: {
        ...userStateData.report_settings,
        visible_options: [],
        available_options: userStateData.report_settings.workspace_options,
        tab_name_arr: tabNameArray,
        tab_type_arr: tabTypeArray,
      },
    })
  );
};

// reset report settings params in redux
export const resetReportSettings = () => {
  store.dispatch(deleteItems(["report_settings"]));
};

export const resetNewSectionDetails = () => {
  store.dispatch(deleteItems(["new_report_section"]));
};

export const resetEditSectionDetails = () => {
  store.dispatch(deleteItems(["edit_report_section"]));
};

// to save new section for the reports
export const saveNewSectionForCampaignReports = async (campaignId) => {
  const userStateData = store.getState().userSelections;

  // call api
  const saveSection = await sendApiRequest(
    "report-setting/add-section",
    { ...userStateData.new_report_section, tab_name: userStateData.new_report_section.tab_name.replace(/\s+/g, "") },
    "POST"
  );
  //   evaluate api response
  if (typeof saveSection.success !== "undefined" && saveSection.success === true) {
    resetNewSectionDetails();
    await fetchReportSettings(campaignId, false);
    toast("New section saved successfully", { type: "success" });
    return {
      success: true,
    };
  } else {
    toast("Sorry, something went wrong.", { type: "error" });
    return {
      success: false,
    };
  }
};

export const validateReportSections = () => {
  const userStateData = store.getState().userSelections;
  const sectionsData = userStateData.edit_report_section;
  let error = false;
  let errorDetails = {};

  sectionsData.forEach((section, index) => {
    const validateTabName = validateEmpty(section.tab_name);
    const validateTabText = validateAlphabets(section.tab_text, true, 25, true);
    if (validateTabName.error === true) {
      error = true;
      errorDetails = {
        ...errorDetails,
        error: true,
        ["error_" + index]: {
          ...errorDetails["error_" + index],
          tab_name: "Dashboard name must not be empty",
        },
      };
    }
    if (validateTabText.error === true) {
      error = true;
      errorDetails = {
        ...errorDetails,
        error: true,
        ["error_" + index]: {
          ...errorDetails["error_" + index],
          tab_text: "Section Name must not be empty and may not contain special characters",
        },
      };
    }
  });

  return {
    error,
    errorDetails,
  };
};

// to update the section names
export const updateSectionsForCampaignReports = async (campaignId) => {
  const userStateData = store.getState().userSelections;
  const validateSectionData = validateReportSections();

  if (validateSectionData.error === true) {
    return {
      success: false,
      error_details: validateSectionData.errorDetails,
    };
  } else {
    // call api
    const saveSettings = await sendApiRequest(
      "report-setting/edit-section",
      {
        sections: userStateData.edit_report_section.map((reportSec) => {
          return {
            ...reportSec,
            tab_name: reportSec.tab_name.replace(/\s+/g, ""),
          };
        }),
      },
      "POST"
    );
    //   evaluate api response
    if (typeof saveSettings.success !== "undefined" && saveSettings.success === true) {
      resetEditSectionDetails();
      await fetchReportSettings(campaignId, false);
      toast("Sections updated successfully", { type: "success" });
      return {
        success: true,
      };
    } else {
      toast("Sorry, something went wrong.", { type: "error" });
      return {
        success: false,
      };
    }
  }
};

export const resetShareModalInfo = () => {
  store.dispatch(deleteItems([
    "send_email_notif",
    "send_personal_message",
    "personalised_message",
    "selected_brand_userforReportshare",
    "show_report_cancel_confirmation",
    "shared_report_users_list"
  ]))
}

const getRichTextPlainText = (html) => {
  const richTextHtml = String(html || "");

  if (typeof DOMParser !== "undefined") {
    const parsedDocument = new DOMParser().parseFromString(richTextHtml, "text/html");
    return parsedDocument.body?.textContent || "";
  }

  return richTextHtml
    .replace(/&nbsp;/gi, " ")
    .replace(/<[^>]*>/g, " ");
};

const isEmptyRichText = (html) =>
  getRichTextPlainText(html).replace(/\u00a0/g, " ").trim() === "";

export const validateReportShareInfo = (campaignId, sendToSelf) => {
  const currentStateData = store.getState().userSelections;
  const emailList =
    typeof currentStateData.selected_brand_userforReportshare !== "undefined" && currentStateData.selected_brand_userforReportshare.length !== 0
      ? currentStateData.selected_brand_userforReportshare.map(email => {
        return email.includes("New:") ? email.split("New: ")[1].trim() : email.trim()
      })
      : [];
  // validate personalised message
  const sendPersonalMessage = typeof currentStateData.send_personal_message !== "undefined" ? currentStateData.send_personal_message : false;

  const sendEmailNotif = typeof currentStateData.send_email_notif !== "undefined" ? currentStateData.send_email_notif : false;

  const personalMessage = typeof currentStateData.personalised_message !== "undefined" ? currentStateData.personalised_message : "";

  console.log("-----------", personalMessage)

  let error = false;
  let errorMessages = {};

  if (sendPersonalMessage === true && isEmptyRichText(personalMessage)) {
    error = true;
    errorMessages = {
      ...errorMessages,
      personal_message: "Please enter the personalized message",
    };
  }

  // validate if the emails are provided or not
  if (emailList.length === 0) {
    // toast("Please fill in valid information", { type: "error" });
    error = true;
    errorMessages = {
      ...errorMessages,
      email_list: "Please enter the E-Mail(s)",
    };
  } else {
    emailList.forEach((email) => {
      const validateFormat = validateEmailFormat(email);
      if (validateFormat === false) {
        error = true;
        errorMessages = {
          ...errorMessages,
          email_list: "Please use a valid e-mail format like name@example.com",
        };
      }
    });
  }

  if (error === true) {
    return {
      success: false,
      error: true,
      errorMessages: errorMessages,
    };
  } else {
    return {
      success: true,
      error: false,
      share_report_request: {
        "id_campaign": campaignId,
        "email_list": emailList,
        "send_to_self": sendToSelf,
        "send_email_notif": sendEmailNotif,
        "message": sendPersonalMessage === true ? personalMessage : ""
      },
    };
  }
};

// share report 
export const shareReport = async (campaignId, sendToSelf) => {
  const validateData = validateReportShareInfo(campaignId, sendToSelf);

  // validate if the emails are provided or not
  if (validateData.error === true) {
    toast("Please fill in valid information", { type: "error" });
    return {
      success: false,
      error: true,
      errorMessages: validateData.errorMessages,
    };
  } else {
    // send api request
    const sendInfo = await sendApiRequest("email/share-campaign", validateData.share_report_request, "POST");

    if (typeof sendInfo.success !== "undefined" && sendInfo.success === true) {
      toast("Report shared successfully", { type: "success" });
      fetchListOfSharedUsers(campaignId);
      loadCampaigns();
      return {
        success: true,
        message: "Report shared successfully",
      };
    } else {
      toast("Sorry, something went wrong", { type: "error" });
      return {
        success: true,
        message: "Sorry, something went wrong",
      };
    }
  }
};

// send test email to self
export const sendTestEmailToSelf = async (campaignId) => {
  const currentStateData = store.getState().userSelections;
  // current user details
  const loggedInUserDetail = checkUserDetailsFromCookie();

  // check if personalised message set to true
  const sendPersonalMessage = typeof currentStateData.send_personal_message !== "undefined" ? currentStateData.send_personal_message : false;
  // personalised message var
  const personalMessage = typeof currentStateData.personalised_message !== "undefined" ? currentStateData.personalised_message : "";

  let share_report_request = {
    "id_campaign": campaignId,
    "email_list": [loggedInUserDetail?.user_detail?.user_email],
    "send_to_self": true,
    "send_email_notif": true,
    "message": sendPersonalMessage === true ? personalMessage : ""
  }

  // send api request
  const sendInfo = await sendApiRequest("email/share-campaign", share_report_request, "POST");

  if (typeof sendInfo.success !== "undefined" && sendInfo.success === true) {
    toast("Test message sent", { type: "success" });
    return {
      success: true,
      message: "Test message sent",
    };
  } else {
    toast("Sorry, something went wrong", { type: "error" });
    return {
      success: true,
      message: "Sorry, something went wrong",
    };
  }
};

// to fetch list of users with whom report is shared
export const fetchListOfSharedUsers = async (campaignId) => {
  const currentStateData = store.getState().userSelections;
  // send api request
  const fetchSharedUsers = await sendApiRequest("campaigns/" + campaignId + "/shared-users", {}, "GET");

  if (typeof fetchSharedUsers.success !== "undefined" && fetchSharedUsers.success === true) {
    store.dispatch(
      updateStateData({
        shared_report_users_list: fetchSharedUsers.response,
      })
    );
    return {
      success: true,
      message: "Test message sent",
    };
  } else {
    toast("Sorry, something went wrong", { type: "error" });
    return {
      success: true,
      message: "Sorry, something went wrong",
    };
  }
}

// to revoke shared access from a user
export const revokeSharedAccessFromUser = async (campaignId, userEmail) => {
  // send api request
  const revokeAccess = await sendApiRequest("campaigns/" + campaignId + "/shared-users/remove", { email_list: [userEmail] }, "POST");
  if (typeof revokeAccess.success !== "undefined" && revokeAccess.success === true) {
    toast("Access revoked successfully", { type: "success" });
    fetchListOfSharedUsers(campaignId);
    loadCampaigns();
    return {
      success: true,
      message: "Access revoked successfully",
    };
  } else {
    toast("Sorry, something went wrong", { type: "error" });
    return {
      success: true,
      message: "Sorry, something went wrong",
    };
  }
};

// Section-aware seeding lives in dashboardFilterSeed.js.
// These wrappers keep existing call sites working (Brand Report popup, older loaders).
export const defineSectionNamesForFilter = (userChoices) => {
  return buildAnalysisGroupKeysForSections(userChoices);
};

export const defineSectionNamesForRegionFilter = () => {
  return buildRegionKeysForSections();
};

export const defineSectionNamesForNielsenRegionFilter = () => {
  return buildNielsenKeysForSections();
};

export const fetchReportTabsConfig = () => {
  const currentStateData = store.getState().userSelections;

  const impressionDashboardId = typeof currentStateData.report_settings !== "undefined" &&
    typeof currentStateData.report_settings.available_options !== "undefined" ? currentStateData.report_settings.available_options.filter((impressionOpt) => impressionOpt.type === "impression").map((impressionOpt) => impressionOpt.id) : [];

  const visibleOptionIds = typeof currentStateData.report_settings !== "undefined" &&
    typeof currentStateData.report_settings.visible_options !== "undefined" ? currentStateData.report_settings.visible_options : [];

  // Merge and dedupe IDs
  const uniqueIds = [...new Set([...impressionDashboardId, ...visibleOptionIds])];

  const tabsConfig = uniqueIds.length !== 0 ?
    uniqueIds.map(visibleSecId => {
      return currentStateData.report_settings.available_options.find(workOpt => workOpt.id === visibleSecId)
    })
    :
    [];

  return tabsConfig;
}

export const fetchReportTabsConfigForPublishedReport = (viewMode) => {
  const currentStateData = store.getState().userSelections;
  const fetchConfigs = fetchReportTabsConfig();
  const checkUserDetails = checkUserDetailsFromCookie();
  // Published tabs: brand always. Otherwise the campaign owner workspace, or a user who can edit across companies.
  const visibilityCondition =
    viewMode === "Brand"
      ? true
      : checkUserDetails?.user_detail?.current_workspace?.name === currentStateData.campaign_detail.owner_company_name
        || canEditAcrossCompanies(checkUserDetails?.user_detail?.user_type) === true;

  // Impression tab: brand always; otherwise look up this campaign's root retailer
  // in report_section_availability.impression_sections (same rule as campaign list).
  const rootRetailerId = currentStateData.campaign_detail?.root_retailer_company?.id;
  const impressionAvailable =
    viewMode === "Brand"
      ? true
      : currentStateData.report_section_availability?.impression_sections?.[rootRetailerId] === true;

  let publishedTabs = [];
  // add impression dashboard (if available)
  const impressionTab = fetchConfigs.find(tab => tab.type === "impression");
  if (impressionAvailable === true && currentStateData.campaign_detail.impression_report_status === 1 && typeof impressionTab !== "undefined") {
    publishedTabs = [...publishedTabs, impressionTab];
  }
  // add performance dashboards
  if (currentStateData.report_settings.published === true && currentStateData.campaign_detail.campaign_status_code === 4 && visibilityCondition === true) {
    publishedTabs = [...publishedTabs, ...fetchConfigs.filter(tab => tab.type !== "impression")];
  }
  return publishedTabs;
}

function fetchFirstAnalysisGroup(analysisGroups) {
  const filterGroups =
    typeof analysisGroups !== "undefined" && analysisGroups.length !== 0
      ? [...analysisGroups].filter((item) => item !== "(All)")
      : [];
  return filterGroups.length !== 0 ? filterGroups[0] : "";
}

export const defineDashboardVariables = (configTabType, configTabName, configFilterArr, viewMode) => {
  const currentStateData = store.getState().userSelections;
  const reduxKeywordToSaveValue = configTabType === "impression" ? "embedded_impression_filter" : "embedded_performance_filter";
  const selectedDataFilterKeyword = viewMode === "Brand" ? "brand_filters" : "retailer_filters";

  // with RIA, we need to add the execution campaign id to the dashboard variables
  const campaignIdFallback = typeof currentStateData.campaign_detail !== "undefined" ? currentStateData.campaign_detail.campaign_id : ""
  let dashboardVariables = {
    IdCampaign: typeof currentStateData.campaign_detail !== "undefined" ? currentStateData.campaign_detail.execution_data_for_embeddable : campaignIdFallback
  };

  // Mandatory brand-sharing variables: always sent to Embeddable even when the matching
  // filter widget is not configured/visible on this dashboard section.
  // Stores applies to both impression and performance (impressions already rely on this).
  if (typeof currentStateData[reduxKeywordToSaveValue] !== "undefined" && typeof currentStateData[reduxKeywordToSaveValue].store_ids !== "undefined") {
    dashboardVariables = {
      ...dashboardVariables,
      Stores: currentStateData[reduxKeywordToSaveValue].store_ids,
    }
  }

  // Week + AnalysisGroup are mandatory on performance dashboards only.
  // Brand View and brand access both read weeks/groups from brand_filters / retailer_filters.
  if (configTabType !== "impression") {
    dashboardVariables = {
      ...dashboardVariables,
      Week: currentStateData[selectedDataFilterKeyword]?.performance_filter?.weeks
    }

    // Prefer the per-tab selection (used when AnalysisGroup is visible); otherwise use the
    // brand-sharing analysis_group values so hidden dashboards still get the constraint.
    const analysisGroupParamName = configTabName.replace(" ", "_");
    const perSectionGroups = currentStateData[reduxKeywordToSaveValue]?.[analysisGroupParamName + "_selected_analysis_group"];
    const brandSharingGroups = currentStateData[selectedDataFilterKeyword]?.performance_filter?.analysis_group;
    dashboardVariables = {
      ...dashboardVariables,
      AnalysisGroup: (typeof perSectionGroups !== "undefined" && perSectionGroups.length !== 0)
        ? perSectionGroups
        : (brandSharingGroups || [])
    }
  }

  // setting up variables for dashboard
  // Visible-filter handlers below may override Stores / AnalysisGroup (e.g. single-select).
  configFilterArr.forEach((filter) => {
    // metrics swap
    if (filter.keyword === "AbsolutePercentage") {
      dashboardVariables = {
        ...dashboardVariables,
        [filter.keyword]: typeof currentStateData[reduxKeywordToSaveValue] !== "undefined" && typeof currentStateData[reduxKeywordToSaveValue][configTabName.replace(" ", "_") + "_absolute_percentage"] !== "undefined" ? currentStateData[reduxKeywordToSaveValue][configTabName.replace(" ", "_") + "_absolute_percentage"] : false,
      }
    }
    // stores filter (multi select) — same values as the mandatory Stores default when visible
    if (filter.keyword === "Stores" && filter.type === "multiSelect") {
      if (typeof currentStateData[reduxKeywordToSaveValue] !== "undefined" && typeof currentStateData[reduxKeywordToSaveValue].store_ids !== "undefined") {
        dashboardVariables = {
          ...dashboardVariables,
          [filter.keyword]: currentStateData[reduxKeywordToSaveValue].store_ids,
        }
      }
    }
    // stores filter (single select) — overrides the mandatory Stores array with the first store
    if (filter.keyword === "Stores" && filter.type === "singleSelect") {

      if (typeof currentStateData[reduxKeywordToSaveValue] !== "undefined" && typeof currentStateData[reduxKeywordToSaveValue].store_ids !== "undefined" && currentStateData[reduxKeywordToSaveValue].store_ids.length !== 0) {
        dashboardVariables = {
          ...dashboardVariables,
          [filter.keyword]: [currentStateData[reduxKeywordToSaveValue].store_ids[0]],
        }
      }
    }
    // date slider
    if (filter.keyword === "Date") {
      if (
        typeof currentStateData[reduxKeywordToSaveValue] !== "undefined" &&
        typeof currentStateData[reduxKeywordToSaveValue].from !== "undefined" &&
        currentStateData[reduxKeywordToSaveValue].from
      ) {
        const endDate = new Date(currentStateData[reduxKeywordToSaveValue].to);
        endDate.setDate(endDate.getDate() + 1); // add one day to

        dashboardVariables = {
          ...dashboardVariables,
          [filter.keyword]: {
            from: currentStateData[reduxKeywordToSaveValue].from,
            to: endDate,
          }
        }
      }
    }
    // Analysis Group (multiselect) — overrides the mandatory default when this filter is visible
    if (filter.keyword === "AnalysisGroup" && filter.type === "multiSelect") {
      const analysisGroupParamName = configTabName.replace(" ", "_");

      if (typeof currentStateData[reduxKeywordToSaveValue] !== "undefined" && typeof currentStateData[reduxKeywordToSaveValue][analysisGroupParamName + "_selected_analysis_group"] !== "undefined" && currentStateData[reduxKeywordToSaveValue][analysisGroupParamName + "_selected_analysis_group"].length !== 0) {
        dashboardVariables = {
          ...dashboardVariables,
          [filter.keyword]: currentStateData[reduxKeywordToSaveValue][analysisGroupParamName + "_selected_analysis_group"],
        }
      } else {
        dashboardVariables = {
          ...dashboardVariables,
          [filter.keyword]: [],
        }
      }
    }
    // Analysis Group (single select) — overrides the mandatory array with the first group
    if (filter.keyword === "AnalysisGroup" && filter.type === "singleSelect") {
      const analysisGroupParamName = configTabName.replace(" ", "_") + "_selected_analysis_group";
      if (typeof currentStateData[reduxKeywordToSaveValue] !== "undefined" && typeof currentStateData[reduxKeywordToSaveValue][analysisGroupParamName] !== "undefined" && currentStateData[reduxKeywordToSaveValue][analysisGroupParamName].length !== 0) {
        dashboardVariables = {
          ...dashboardVariables,
          [filter.keyword]: fetchFirstAnalysisGroup(currentStateData[reduxKeywordToSaveValue][analysisGroupParamName]),
        }
      } else {
        dashboardVariables = {
          ...dashboardVariables,
          [filter.keyword]: "",
        }
      }
    }
    // Rank By
    if (filter.keyword === "RankBy") {
      dashboardVariables = {
        ...dashboardVariables,
        [filter.keyword]: typeof currentStateData[reduxKeywordToSaveValue] !== "undefined" && typeof currentStateData[reduxKeywordToSaveValue].rank_by !== "undefined" ? currentStateData[reduxKeywordToSaveValue].rank_by : "No Ranking",
      }
    }

    // Marketing Activity
    if (filter.keyword === "Activity") {
      dashboardVariables = {
        ...dashboardVariables,
        [filter.keyword]: typeof currentStateData[reduxKeywordToSaveValue] !== "undefined" && typeof currentStateData[reduxKeywordToSaveValue][configTabName.replace(" ", "_") + "_marketing_activity"] !== "undefined" ? currentStateData[reduxKeywordToSaveValue][configTabName.replace(" ", "_") + "_marketing_activity"] : "",
      }
    }

    // there's a new "Activity type" filter that is exactly same as "Activity" filter except that there's one hard coded option added to the list
    if (filter.keyword === "Activity Type") {
      dashboardVariables = {
        ...dashboardVariables,
        "Activity": typeof currentStateData[reduxKeywordToSaveValue] !== "undefined" && typeof currentStateData[reduxKeywordToSaveValue][configTabName.replace(" ", "_") + "_marketing_activity"] !== "undefined" ? currentStateData[reduxKeywordToSaveValue][configTabName.replace(" ", "_") + "_marketing_activity"] : "",
      }
    }

    // regions filter 
    if (filter.keyword === "Regions" && filter.type === "multiSelect") {
      const regionParamName = configTabName.replace(" ", "_");

      if (typeof currentStateData[reduxKeywordToSaveValue] !== "undefined" && typeof currentStateData[reduxKeywordToSaveValue][regionParamName + "_selected_region_name"] !== "undefined" && currentStateData[reduxKeywordToSaveValue][regionParamName + "_selected_region_name"].length !== 0) {
        dashboardVariables = {
          ...dashboardVariables,
          dropdownRegions: currentStateData[reduxKeywordToSaveValue][regionParamName + "_selected_region_name"],
        }
      } else {
        dashboardVariables = {
          ...dashboardVariables,
          dropdownRegions: [],
        }
      }
    }

    if (filter.keyword === "Nielsen Regions" && filter.type === "multiSelect") {
      const regionParamName = configTabName.replace(" ", "_");

      if (typeof currentStateData[reduxKeywordToSaveValue] !== "undefined" && typeof currentStateData[reduxKeywordToSaveValue][regionParamName + "_selected_nielsen_regions"] !== "undefined" && currentStateData[reduxKeywordToSaveValue][regionParamName + "_selected_nielsen_regions"].length !== 0) {
        dashboardVariables = {
          ...dashboardVariables,
          NielsenRegionDropdown: currentStateData[reduxKeywordToSaveValue][regionParamName + "_selected_nielsen_regions"],
        }
      } else {
        dashboardVariables = {
          ...dashboardVariables,
          NielsenRegionDropdown: [],
        }
      }
    }

    if (filter.keyword === "Checkout Data") {
      // default to "basket_trolley" because "basket_trolley" is the first option in the dropdown after applying sort based on label in ascending order
      dashboardVariables = {
        ...dashboardVariables,
        name_checkout: typeof currentStateData[reduxKeywordToSaveValue] !== "undefined" && typeof currentStateData[reduxKeywordToSaveValue][configTabName.replace(" ", "_") + "_checkout_data"] !== "undefined" ? currentStateData[reduxKeywordToSaveValue][configTabName.replace(" ", "_") + "_checkout_data"] : "basket_trolley",
      }
    }

    // Impressions format 
    if (filter.keyword === "absoluteButton") {
      // default to "basket_trolley" because "basket_trolley" is the first option in the dropdown after applying sort based on label in ascending order
      dashboardVariables = {
        ...dashboardVariables,
        absoluteButton: typeof currentStateData[reduxKeywordToSaveValue] !== "undefined" && typeof currentStateData[reduxKeywordToSaveValue][configTabName.replace(" ", "_") + "_impression_format"] !== "undefined" ? currentStateData[reduxKeywordToSaveValue][configTabName.replace(" ", "_") + "_impression_format"] : false,
      }
    }

    // show clip boolean
    // if (filter.keyword === "ShowClips") {
    //   dashboardVariables = {
    //     ...dashboardVariables,
    //     [filter.keyword]: typeof currentStateData[reduxKeywordToSaveValue] !== "undefined" && typeof currentStateData[reduxKeywordToSaveValue][configTabName.replace(" ", "_") + "_show_clip_filter"] !== "undefined" ? currentStateData[reduxKeywordToSaveValue][configTabName.replace(" ", "_") + "_show_clip_filter"] : false,
    //   }
    // }

    // Id Clip filter
    // if (filter.keyword === "IdClip") {
    //   dashboardVariables = {
    //     ...dashboardVariables,
    //     [filter.keyword]: typeof currentStateData[reduxKeywordToSaveValue] !== "undefined" && typeof currentStateData[reduxKeywordToSaveValue][configTabName.replace(" ", "_") + "_idClip_filter"] !== "undefined" ? currentStateData[reduxKeywordToSaveValue][configTabName.replace(" ", "_") + "_idClip_filter"] : [],
    //   }
    // }
    // Id Picture / Loop visual
    // if (filter.keyword === "IdPicture") {
    //   dashboardVariables = {
    //     ...dashboardVariables,
    //     [filter.keyword]: typeof currentStateData[reduxKeywordToSaveValue] !== "undefined" && typeof currentStateData[reduxKeywordToSaveValue][configTabName.replace(" ", "_") + "_idPicture_filter"] !== "undefined" ? currentStateData[reduxKeywordToSaveValue][configTabName.replace(" ", "_") + "_idPicture_filter"] : [],
    //   }
    // }

    // if (filter.keyword === "Status") {
    //   dashboardVariables = {
    //     ...dashboardVariables,
    //     [filter.keyword]: typeof currentStateData[reduxKeywordToSaveValue] !== "undefined" && typeof currentStateData[reduxKeywordToSaveValue][configTabName.replace(" ", "_") + "_status_filter"] !== "undefined" ? currentStateData[reduxKeywordToSaveValue][configTabName.replace(" ", "_") + "_status_filter"] : [],
    //   }
    // }
  });

  return dashboardVariables;
}
